import type { ReactNode } from "react";
import { useInView } from "../../hooks/useInView";

/**
 * Elementos gráficos da landing page: rabiscos à mão (SVG inline), blobs,
 * badges "sticker" e botões pill. Todos usam apenas os tokens da landing
 * (cream / ink / primary) — nunca `neutral-*`, para ficarem imunes ao dark mode.
 */

// ─── Rabisco de sublinhado à mão ──────────────────────────────────────────────
interface ScribbleUnderlineProps {
  children: ReactNode;
  className?: string;
  /** Cor do traço (classe Tailwind de texto, ex: "text-primary-500"). */
  strokeClassName?: string;
}

/** Sublinha a palavra com um traço desenhado à mão que se anima ao entrar na viewport. */
export function ScribbleUnderline({
  children,
  className = "",
  strokeClassName = "text-primary-500",
}: ScribbleUnderlineProps) {
  const { ref, inView } = useInView<HTMLSpanElement>();

  return (
    <span ref={ref} className={`relative inline-block whitespace-nowrap ${className}`}>
      <span className="relative z-10">{children}</span>
      <svg
        className={`absolute -bottom-2 left-0 w-full h-4 ${strokeClassName} ${inView ? "is-visible" : ""}`}
        viewBox="0 0 200 16"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          className="scribble-path"
          pathLength={1}
          d="M4 11 C 40 4, 80 13, 118 8 S 180 5, 196 9"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

// ─── Círculo rabiscado (destaque de preço) ────────────────────────────────────
interface ScribbleCircleProps {
  children: ReactNode;
  className?: string;
  strokeClassName?: string;
}

/** Circula o conteúdo com uma elipse de traço solto, desenhada ao entrar na viewport. */
export function ScribbleCircle({
  children,
  className = "",
  strokeClassName = "text-primary-500",
}: ScribbleCircleProps) {
  const { ref, inView } = useInView<HTMLSpanElement>();

  return (
    <span ref={ref} className={`relative inline-block ${className}`}>
      <span className="relative z-10">{children}</span>
      <svg
        className={`absolute -inset-x-4 -inset-y-2 w-[calc(100%+2rem)] h-[calc(100%+1rem)] ${strokeClassName} ${inView ? "is-visible" : ""}`}
        viewBox="0 0 220 90"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          className="scribble-path"
          pathLength={1}
          d="M118 8 C 40 2, 8 22, 10 46 C 12 74, 66 86, 116 84 C 172 82, 212 68, 210 42 C 208 16, 160 4, 96 9"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

// ─── Setinha desenhada à mão ──────────────────────────────────────────────────
interface HandArrowProps {
  className?: string;
}

/** Seta curvada estilo rabisco, usada entre os passos do "como funciona". */
export function HandArrow({ className = "" }: HandArrowProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 80 40"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 27 C 22 12, 46 20, 68 20"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M59 13 L 70 20 L 59 27"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ─── Blob orgânico ────────────────────────────────────────────────────────────
interface BlobProps {
  className?: string;
}

/** Mancha de cor orgânica para decorar cantos de seção. Puramente decorativa. */
export function Blob({ className = "" }: BlobProps) {
  return (
    <div
      className={`pointer-events-none absolute ${className}`}
      style={{ borderRadius: "42% 58% 60% 40% / 55% 42% 58% 45%" }}
      aria-hidden="true"
    />
  );
}

// ─── Badge "sticker" ──────────────────────────────────────────────────────────
interface StickerBadgeProps {
  children: ReactNode;
  className?: string;
  /** Rotação em graus (levemente torto, estilo adesivo). */
  rotate?: number;
}

/** Pill com borda grossa e leve rotação, estilo adesivo colado na página. */
export function StickerBadge({ children, className = "", rotate = -2 }: StickerBadgeProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border-2 text-xs font-bold ${className}`}
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      {children}
    </span>
  );
}

// ─── Botão pill ───────────────────────────────────────────────────────────────
interface PillButtonProps {
  children: ReactNode;
  onClick?: () => void;
  id?: string;
  className?: string;
  /** Sombra dura deslocada (assinatura visual da landing). Classe de cor da sombra. */
  hardShadow?: "primary" | "ink" | "none";
  type?: "button" | "submit";
  disabled?: boolean;
}

const hardShadowClasses: Record<NonNullable<PillButtonProps["hardShadow"]>, string> = {
  primary: "shadow-[4px_4px_0_0_var(--color-primary-500)] hover:shadow-[6px_6px_0_0_var(--color-primary-500)]",
  ink: "shadow-[4px_4px_0_0_var(--color-ink)] hover:shadow-[6px_6px_0_0_var(--color-ink)]",
  none: "",
};

/** Botão arredondado (pill) com hover de elevação e sombra dura opcional. */
export function PillButton({
  children,
  onClick,
  id,
  className = "",
  hardShadow = "none",
  type = "button",
  disabled,
}: PillButtonProps) {
  return (
    <button
      id={id}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2.5 rounded-full font-bold transition-all duration-200 hover:-translate-y-0.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 ${hardShadowClasses[hardShadow]} ${className}`}
    >
      {children}
    </button>
  );
}
